import * as authActions from '../actions/auth.actions';
import * as fromCore from '../reducers/index';

import { Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AuthService,
  LoginApiError,
  LoginResponse,
} from '../services/auth.service';
import { Observable, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { LoggerService } from '@services/logger.service';
import { Router } from '@angular/router';
import { TabSessionService } from '../services/tab-session.service';
import { ToastService } from '@services/toast.service';
import { UserService } from '../services/user.service';

type LoginSuccessAction = ReturnType<typeof authActions.loginSuccess>;
@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private userService: UserService,
    private toastService: ToastService,
    private router: Router,
    private store: Store,
    private logger: LoggerService,
    private tabSessionService: TabSessionService,
    private queryClient: QueryClient,
  ) {}

  login$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.login),
      switchMap((action) =>
        this.authService.login(action.credentials).pipe(
          map((response: LoginResponse) => {
            return authActions.loginSuccess({
              response,
              remember: action.credentials.remember_me || false,
            });
          }),
          catchError((error: LoginApiError) =>
            of(authActions.loginFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.loginSuccess, authActions.remoteLoginSuccess),
        withLatestFrom(this.store.select(fromCore.selectRedirectUrl)), // get redirectUrl from store
        tap(
          ([action, redirectUrl]: [LoginSuccessAction, string | undefined]) => {
            this.authService.setCredentials(
              {
                token: action.response.access_token,
                expiresAt:
                  Number(action.response.access_token_expires_at) || undefined,
              },
              action.remember,
            );

            // Initialize per-tab company context from the user's active company
            const activeCompanyId = action.response.user?.active_company_id;
            if (
              activeCompanyId &&
              !this.tabSessionService.getActiveCompanyId()
            ) {
              this.tabSessionService.setActiveCompanyId(activeCompanyId);
            }

            // If there's a redirectUrl, navigate to it
            if (redirectUrl) {
              this.logger.debug('Redirecting to', redirectUrl);
              this.router.navigateByUrl(redirectUrl);
            }
          },
        ),
      ),
    { dispatch: false },
  );

  logout$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logout),
      tap(() => this.userService.clearCaches()), // <-- clear User service caches regardless of the success of the logout
      switchMap(() =>
        this.authService.logout().pipe(
          map(() => {
            return authActions.logoutSuccess();
          }),
          catchError((error) => of(authActions.logoutFailure({ error }))),
        ),
      ),
    ),
  );

  authCheck$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.authCheck),
        // NOTE: no take(1) here — it completed the whole effect after the first
        // authCheck, so later dispatches were silently ignored
        withLatestFrom(this.store.pipe(select(fromCore.selectIsAuthenticated))),
        tap(([action, isAuthenticated]) => {
          const onAuthRoute =
            this.router.url.startsWith('/auth') ||
            window.location.pathname.startsWith('/auth');
          if (!isAuthenticated && !onAuthRoute) {
            // Navigate only if the user is not authenticated and not already on a public auth page
            // (login, forgot-password, create-password/:token, reset-password/:token, validate-account, remote/:authToken)
            this.router
              .navigate(['/auth/login'])
              .then(() => {
                // Handle successful navigation if needed
              })
              .catch((error) => {
                // Handle navigation error
                console.error('Navigation error in authCheck$ effect', error);
              });
          }
        }),
        catchError((error) => {
          // Handle the error from the entire observable chain
          console.error('Error in authCheck$ effect', error);
          return of({ type: '[Auth] Auth Check Error', error });
        }),
      ),
    { dispatch: false },
  );

  /**
   * Every cached server response belongs to the session and company that fetched
   * it, so the whole query cache is dropped when either changes - the same
   * trigger pair the hand-rolled `clearCache()` services listen on.
   */
  clearQueryCacheOnSessionChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.logout, authActions.switchUserCompany),
        tap(() => this.queryClient.clear()),
      ),
    { dispatch: false },
  );

  remoteLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.remoteLogin),
      switchMap(({ token }) =>
        this.authService.loginToken(token).pipe(
          map((response) => {
            if ((response as any)?.error) {
              return authActions.remoteLoginFail({
                error: (response as any).message || 'Login failed',
              });
            }

            return authActions.remoteLoginSuccess({
              response: response as LoginResponse,
            });
          }),
          catchError((err) =>
            of(
              authActions.remoteLoginFail({
                error:
                  err?.error?.message || err?.error?.error || 'Login failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  remoteLoginFailUi$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.remoteLoginFail),
        tap(({ error }) => {
          this.toastService.showError(error || 'Login failed');
          this.router.navigate(['/auth/login']);
        }),
      ),
    { dispatch: false },
  );
}
